/**
 * 💾 **내 컴퓨터로 받기** — 브라우저에서 파일을 저장하게 만드는 한 곳.
 *
 * ## 왜 `<a download>` 만으로 안 되나
 * 관리자는 `admin.woorinabiglobal.com`, 소리·그림은 `woorinabiglobal.com` 에 있다.
 * **다른 출처**라 브라우저는 `download` 속성을 **무시하고 그냥 열어 버린다** — mp3 면
 * 새 탭에서 재생이 시작되고, 운영자는 「저장이 왜 안 되지」만 겪는다.
 *
 * 그래서 한 번 받아서(fetch) blob 으로 만든 뒤 저장한다. 두 출처 모두
 * `Access-Control-Allow-Origin: *` 이라 받아올 수 있다(2026-08-23 확인).
 *
 * ## 다 쓴 주소는 반드시 놓아준다
 * `createObjectURL` 은 놓아주기 전까지 그 파일을 **통째로 메모리에 붙들고 있다.**
 * 34쪽 두 말이면 4MB 다. 한 번은 괜찮지만 여러 번 받으면 쌓인다.
 */

/**
 * 바이트 → Blob.
 *
 * TypeScript 5.7 부터 `Uint8Array` 가 `ArrayBufferLike`(공유 메모리 포함)를 품어
 * `BlobPart` 로 바로 못 넘긴다. 우리가 만든 건 늘 보통 `ArrayBuffer` 라 잘라 넘기면 된다.
 */
export function bytesBlob(bytes: Uint8Array, type: string): Blob {
  const ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  return new Blob([ab], { type });
}

/** 만든 blob 을 파일로 저장. 이름은 그대로 쓰인다(같은 출처의 blob 이라 `download` 가 먹는다). */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  // 파이어폭스는 문서에 붙어 있지 않은 <a> 의 click 을 무시한다 — 붙였다 뗀다.
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 클릭 직후 놓아주면 크롬이 받다 만다. 한 박자 뒤에.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * 다른 출처의 파일을 바이트로 받아 온다.
 *
 * 실패를 **삼키지 않는다** — 34개 중 3개가 조용히 빠지면 운영자는 그 사실을 모른 채
 * 「다 받았다」고 생각한다. 부르는 쪽이 몇 개가 실패했는지 말할 수 있게 던진다.
 */
export async function fetchBytes(url: string): Promise<Uint8Array> {
  const res = await fetch(url, { cache: "no-cache" }).catch(() => {
    throw new Error("파일을 받아오지 못했어요(서버에 연결 실패).");
  });
  if (!res.ok) throw new Error(`파일을 받아오지 못했어요(${res.status}).`);
  return new Uint8Array(await res.arrayBuffer());
}

/**
 * 엑셀이 UTF-8 CSV 를 한글로 읽게 하는 **BOM**. 안 붙이면 「쪽,말,파일」이 깨져 보인다 —
 * 파일은 멀쩡한데 엑셀만 그러니, 받은 사람은 우리가 잘못 만든 줄 안다.
 */
export function csvBytes(text: string): Uint8Array {
  const body = new TextEncoder().encode(text);
  const out = new Uint8Array(3 + body.length);
  out.set([0xef, 0xbb, 0xbf], 0);
  out.set(body, 3);
  return out;
}

/**
 * 🏷 **받은 파일 이름** — 자리 이름을 그대로 쓴다(`hero.base.hero_1.side.still.webp`).
 *
 * 예쁜 이름(「탐험가 옆모습」)이 아니라 **자리 이름**인 이유: 받은 그림을 나중에 다시 올릴 때
 * 어느 칸이었는지 알아야 한다. 폴더에 서른 장이 쌓이면 「탐험가 옆모습2」가 어느 자리였는지
 * 아무도 모른다. 사람이 읽을 이름은 같이 받는 목록(csv)에 적는다.
 */
export function assetFileName(slotKey: string, url: string, type?: string): string {
  const safe = slotKey.replace(/[\\:*?"<>|/]/g, "_") || "asset";
  // 주소에서 확장자를 먼저 본다 — R2 파일 이름이 해시라도 확장자는 살아 있다.
  const m = /\.([a-z0-9]{2,4})(?:[?#]|$)/i.exec(url);
  const fromUrl = m?.[1]?.toLowerCase();
  const fromType = type?.split("/")[1]?.split(";")[0]?.replace("jpeg", "jpg");
  const ext = fromUrl ?? fromType ?? "bin";
  return `${safe}.${ext}`;
}

/**
 * 한 자리의 그림을 내 컴퓨터로.
 *
 * `data:` 로 들어 있는 그림(아직 안 올린 것)도 같은 길로 받는다 — 두 갈래로 나누면
 * 한쪽에만 이름 규칙이 빠진다.
 */
export async function saveAssetUrl(slotKey: string, url: string): Promise<void> {
  const res = await fetch(url, { cache: "no-cache" }).catch(() => {
    throw new Error("그림을 받아오지 못했어요(서버에 연결 실패).");
  });
  if (!res.ok) throw new Error(`그림을 받아오지 못했어요(${res.status}).`);
  const blob = await res.blob();
  saveBlob(blob, assetFileName(slotKey, url, blob.type));
}
